import { useEffect, useState, useRef } from 'react';
import { CameraIcon } from '@heroicons/react/20/solid';
import { FORMAT, formatDate, resizeImgFile } from '../../../../lib/utils';
import { ReactComponent as CloseSvg } from '../../../../assets/svg/closeCircle.svg';
import { ReactComponent as LoadingSvg } from '../../../../assets/svg/loading-half-status.svg';
import axios from 'axios';
import request from '../../../../lib/request';

const ANGLE = {
  FRONT: 'front',
  BACK: 'back',
  SIDE: 'side',
};

const AddPhoto = ({
  label,
  source,
  fileRef,
  handleCapture,
  handleClickCapture,
  handleRemove,
}) => {
  const inputId = Math.random().toString();
  return (
    <div className='relative'>
      <div
        className={`relative flex justify-center aspect-square rounded-xl overflow-hidden border border-tertiary mb-2 ${
          source ? '' : 'border-dashed'
        }`}
      >
        {source ? (
          <div className='w-full h-full'>
            <img
              src={source}
              alt='source'
              className='w-full h-full object-cover'
            />
          </div>
        ) : (
          <div className='flex flex-col justify-center items-center w-full h-full'>
            <input
              ref={fileRef}
              accept='image/*'
              id={inputId}
              type='file'
              onChange={handleCapture}
              onClick={handleClickCapture}
              className='hidden'
            />
            <label
              htmlFor={inputId}
              className='flex justify-center items-center w-full h-full text-white'
            >
              <CameraIcon className='size-8' />
            </label>
          </div>
        )}
      </div>
      {source && (
        <div className='absolute top-0 right-0 opacity-80'>
          <button onClick={handleRemove}>
            <CloseSvg className='size-7' />
          </button>
        </div>
      )}
      <div className='text-center text-white/50 text-xs'>{label}</div>
    </div>
  );
};

const CreateDialog = ({ dialogRef, closeDialog, isDialogOpen, refresh }) => {
  const frontFileRef = useRef(null);
  const sideFileRef = useRef(null);
  const backFileRef = useRef(null);

  const [date, setDate] = useState(formatDate(new Date()));
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [frontFile, setFrontFile] = useState(null);
  const [sideFile, setSideFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontSource, setFrontSource] = useState('');
  const [sideSource, setSideSource] = useState('');
  const [backSource, setBackSource] = useState('');

  useEffect(() => {
    if (isDialogOpen) {
      setDate(formatDate(new Date()));
    }
  }, [isDialogOpen]);

  const resetState = () => {
    setNote('');
    setFrontFile(null);
    setSideFile(null);
    setBackFile(null);
    setFrontSource('');
    setSideSource('');
    setBackSource('');
  };

  const uploadFile = async (file) => {
    const { result } = await request.get(
      `/upload?filename=${file.name}&contentType=${file.type}&type=body`
    );

    if (!result?.url || !result?.key) {
      throw new Error('Failed to upload');
    }

    await axios.put(result.url, file, {
      headers: {
        'Content-Type': file.type,
      },
    });

    return result;
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      let frontResult, sideResult, backResult;

      if (frontFile) {
        frontResult = await uploadFile(frontFile);
      }
      if (sideFile) {
        sideResult = await uploadFile(sideFile);
      }
      if (backFile) {
        backResult = await uploadFile(backFile);
      }

      const body = {
        logDate: date,
        logTime: formatDate(new Date(), FORMAT.HOURMIN),
        note,
        frontPhotoKey: frontResult?.key,
        sidePhotoKey: sideResult?.key,
        backPhotoKey: backResult?.key,
      };

      await request.post('/log/body', body);

      await refresh();

      resetState();
      closeDialog();
    } catch (err) {
      if (typeof err?.message === 'string') {
        alert(err);
      } else {
        alert('Failed to create');
      }
    }

    setLoading(false);
  };

  const handleCancel = () => {
    resetState();
    closeDialog();
  };

  const handleChangeNote = (e) => {
    if (!loading) {
      setNote(e.target.value);
    }
  };

  const handleClickCapture = (angle) => () => {
    if (angle === ANGLE.FRONT) {
      if (frontFileRef.current && frontFileRef.current.value) {
        frontFileRef.current.value = '';
      }
    }
    if (angle === ANGLE.SIDE) {
      if (sideFileRef.current && sideFileRef.current.value) {
        sideFileRef.current.value = '';
      }
    }
    if (angle === ANGLE.BACK) {
      if (backFileRef.current && backFileRef.current.value) {
        backFileRef.current.value = '';
      }
    }
  };

  const handleCapture = (angle) => async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      let resizedFile = await resizeImgFile(file);

      if (file.size < resizedFile.size) {
        resizedFile = file;
      }

      const newUrl = URL.createObjectURL(resizedFile);

      if (angle === ANGLE.FRONT) {
        setFrontFile(resizedFile);
        setFrontSource(newUrl);
      }
      if (angle === ANGLE.SIDE) {
        setSideFile(resizedFile);
        setSideSource(newUrl);
      }
      if (angle === ANGLE.BACK) {
        setBackFile(resizedFile);
        setBackSource(newUrl);
      }
    }
  };

  const handleRemove = (angle) => () => {
    if (angle === ANGLE.FRONT) {
      setFrontFile(null);
      setFrontSource('');
    }
    if (angle === ANGLE.SIDE) {
      setSideFile(null);
      setSideSource('');
    }
    if (angle === ANGLE.BACK) {
      setBackFile(null);
      setBackSource('');
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className='w-full h-full max-w-lg max-h-full bg-transparent'
      // onCancel={handleCancel}
    >
      <div className='bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl p-4 flex flex-col text-white overflow-y-hidden'>
        <div className='overflow-y-auto scrollbar-hidden rounded-xl pb-8'>
          <div className='flex justify-end mb-8'>
            <button onClick={handleCancel}>
              <CloseSvg className='size-6' />
            </button>
          </div>
          <div className='grid grid-cols-3 gap-4 mb-4'>
            <AddPhoto
              label='Front'
              source={frontSource}
              fileRef={frontFileRef}
              handleCapture={handleCapture(ANGLE.FRONT)}
              handleClickCapture={handleClickCapture(ANGLE.FRONT)}
              handleRemove={handleRemove(ANGLE.FRONT)}
            />
            <AddPhoto
              label='Side'
              source={sideSource}
              fileRef={sideFileRef}
              handleCapture={handleCapture(ANGLE.SIDE)}
              handleClickCapture={handleClickCapture(ANGLE.SIDE)}
              handleRemove={handleRemove(ANGLE.SIDE)}
            />
            <AddPhoto
              label='Back'
              source={backSource}
              fileRef={backFileRef}
              handleCapture={handleCapture(ANGLE.BACK)}
              handleClickCapture={handleClickCapture(ANGLE.BACK)}
              handleRemove={handleRemove(ANGLE.BACK)}
            />
          </div>
          <div className='flex items-center justify-between gap-2'>
            <label className='text-white font-semibold'>Date</label>
            <input
              type='date'
              max={formatDate(new Date(), FORMAT.DATEHOURMIN)}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className='rounded-full px-2 py-1 bg-tertiary  text-white'
            />
          </div>
          <div className='flex flex-col gap-2 mt-2'>
            <label className='text-white font-semibold'>Note</label>
            <textarea
              value={note}
              rows={2}
              onChange={handleChangeNote}
              className='rounded-xl px-3 py-2 bg-tertiary  text-white resize-none'
            />
          </div>
          <div className='mt-2'>
            <button
              className='rounded-full w-full py-2 bg-accent font-bold text-black'
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <div className='flex justify-center items-center'>
                  <LoadingSvg className='animate-spin' />
                </div>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
};

export default CreateDialog;
